// src/components/FileList.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Download,
  Delete,
  Visibility,
  CheckCircle,
  Error,
  HourglassTop,
  CloudUpload,
} from '@mui/icons-material';
import { rosbagAPI, downloadBlob, formatFileSize, formatDuration } from "../../../axiosApi";

const DataManagement = ({ refreshTrigger }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, file: null });
  const [topicsDialog, setTopicsDialog] = useState({ open: false, topics: [], fileName: '' });

  useEffect(() => {
    fetchFiles();
  }, [refreshTrigger]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      const response = await rosbagAPI.getFiles();
      console.log(response.data);
      setFiles(response.data);
      setError(null);
    } catch (error) {
      console.error('파일 목록 조회 실패:', error);
      setError('파일 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = await rosbagAPI.downloadFile(file.id);
      downloadBlob(response.data, file.original_filename);
    } catch (error) {
      console.error('파일 다운로드 실패:', error);
      setError('파일 다운로드에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    try {
      await rosbagAPI.deleteFile(deleteDialog.file.id);
      setFiles(files.filter(f => f.id !== deleteDialog.file.id));
      setDeleteDialog({ open: false, file: null });
    } catch (error) {
      console.error('파일 삭제 실패:', error);
      setError('파일 삭제에 실패했습니다.');
    }
  };

  const handleViewTopics = async (file) => {
    try {
      const formData = new FormData();
      formData.append('bag_file', file.original_filename);

      if (file.original_filename.split('.').pop() === "bag") {
        const response = await rosbagAPI.getRos1Topics(formData);
        setTopicsDialog({
          open: true,
          topics: response.data.topics,
          fileName: file.name
        });
      } else {
        const response = await rosbagAPI.getRos2Topics(formData);
        setTopicsDialog({
          open: true,
          topics: response.data.topics,
          fileName: file.name
        });
      }
    
    } catch (error) {
      console.error('토픽 정보 조회 실패:', error);
      setError('토픽 정보를 불러오는데 실패했습니다.');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      completed: { label: '완료', color: 'success', icon: <CheckCircle /> },
      processing: { label: '처리중', color: 'warning', icon: <HourglassTop /> },
      uploading: { label: '업로드중', color: 'info', icon: <CloudUpload /> },
      error: { label: '오류', color: 'error', icon: <Error /> },
    };

    const config = statusConfig[status] || statusConfig.error;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
        variant="outlined"
      />
    );
  };

  const columns = [
    {
      field: 'name',
      headerName: '파일명',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'file_size',
      headerName: '크기',
      width: 120,
      renderCell: (params) => formatFileSize(params.value),
    },
    {
      field: 'duration',
      headerName: '재생시간',
      width: 120,
      renderCell: (params) => 
        params.value ? formatDuration(params.value) : '-',
    },
    {
      field: 'message_count',
      headerName: '메시지 수',
      width: 120,
      renderCell: (params) => 
        params.value ? params.value.toLocaleString() : '-',
    },
    {
      field: 'uploaded_at',
      headerName: '업로드 일시',
      width: 180,
      renderCell: (params) => 
        new Date(params.value).toLocaleString('ko-KR'),
    },
    {
      field: 'processing_status',
      headerName: '상태',
      width: 120,
      renderCell: (params) => getStatusChip(params.value),
    },
    {
      field: 'actions',
      headerName: '작업',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="토픽 보기">
            <IconButton
              size="small"
              onClick={() => handleViewTopics(params.row)}
              disabled={params.row.processing_status !== 'completed'}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="다운로드">
            <IconButton
              size="small"
              onClick={() => handleDownload(params.row)}
              disabled={params.row.processing_status !== 'completed'}
            >
              <Download />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="삭제">
            <IconButton
              size="small"
              onClick={() => setDeleteDialog({ open: true, file: params.row })}
              color="error"
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          ROS Bag 파일 목록
        </Typography>
        <LinearProgress />
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        ROS Bag 파일 목록 ({files.length}개)
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={files}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[5, 10, 20]}
          disableSelectionOnClick
          getRowId={(row) => row.id}
        />
      </Box>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, file: null })}
      >
        <DialogTitle>파일 삭제 확인</DialogTitle>
        <DialogContent>
          <Typography>
            '{deleteDialog.file?.name}' 파일을 삭제하시겠습니까?
            이 작업은 되돌릴 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, file: null })}>
            취소
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      {/* 토픽 정보 다이얼로그 */}
      <Dialog
        open={topicsDialog.open}
        onClose={() => setTopicsDialog({ open: false, topics: [], fileName: '' })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>토픽 정보 - {topicsDialog.fileName}</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 400, width: '100%' }}>
            <DataGrid
              rows={topicsDialog.topics.map((topic, index) => ({
                id: index,
                ...topic
              }))}
              columns={[
                { field: 'id', headerName: 'ID', width: 90 },
                { field: 'topic', headerName: '토픽명', flex: 1 },
                { field: 'msg_type', headerName: '메시지 타입', flex: 1 },
                { 
                  field: 'message_count', 
                  headerName: '메시지 수', 
                  width: 120
                },
                { 
                  field: 'frequency', 
                  headerName: '주파수 (Hz)', 
                  width: 120
                },
              ]}
              disableSelectionOnClick
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTopicsDialog({ open: false, topics: [], fileName: '' })}>
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DataManagement;